import { useCallback, useEffect, useMemo, useRef } from "react";

import { useSearchParams } from "react-router-dom";

import { useOrganizationUser } from "src/api/taktile/queries";
import { DecisionEnvironment } from "src/clients/jobs/api";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useDatasets } from "src/datasets/api/queries";
import { useMetricsQueryData } from "src/flowGraph/useJobRunCounts";
import { useJobSources, useJobs, useRuns } from "src/jobs/api/queries";
import { JobRun, JobRunStatusType, TestRunJobRun } from "src/jobs/types";
import { aggregateStatusCounts } from "src/performance/nodeStatusUtils";
import {
  useAuthoringContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import { LeftPaneOptions, URLKeys } from "src/router/SearchParams";
import { useGraphStore } from "src/store/StoreProvider";
import { formatDate } from "src/utils/datetime";

const useTestRunJob = (flowId: string, flowVersionId: string) => {
  const { workspace } = useWorkspaceContext();
  const { data: jobs, isLoading } = useJobs({
    baseUrl: workspace.base_url!,
    flowId,
    flowVersionId,
    includeTestRuns: true,
  });

  return {
    testRunJob: jobs?.find((job) => job.tags?.test_run === true),
    isLoading,
  };
};

export const useTestRunRuns = (flowId: string, flowVersionId: string) => {
  const { workspace } = useWorkspaceContext();
  const { testRunJob, isLoading: isTestRunJobLoading } = useTestRunJob(
    flowId,
    flowVersionId,
  );

  const noTestRunJob = !isTestRunJobLoading && testRunJob == undefined;

  const {
    data: testRunRuns,
    isLoading: isRunsLoading,
    isPending,
  } = useRuns(workspace.base_url!, testRunJob?.id ?? "", {
    environment: DecisionEnvironment.TEST,
  });

  const { data: sources, isLoading: isSourcesLoading } = useJobSources(
    workspace.base_url!,
    testRunJob?.id ?? "",
  );

  const sourceIdToDatasetId = useMemo(() => {
    return (
      sources?.reduce(
        (acc, source) => {
          if (source && source.configuration.type === "dataset") {
            acc[source.id] = source.configuration.dataset_id;
          }
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {}
    );
  }, [sources]);

  const isRunning = useMemo(() => {
    return (
      testRunRuns?.pages
        .flat()
        .some((run) => run.status?.type === JobRunStatusType.Running) ?? false
    );
  }, [testRunRuns]);

  return {
    isPending,
    isRunning,
    sourceIdToDatasetId,
    isLoading: isTestRunJobLoading || isRunsLoading || isSourcesLoading,
    testRunRuns: noTestRunJob ? [] : testRunRuns?.pages.flat(),
  };
};

export const STOP_TEST_RUN_EVENT = "stop-test-run";

export type StopTestRunEventDetails = {
  source: "sidepane" | "run-button";
};

export const useStopTestRunListener = (
  callback: (event: CustomEvent<StopTestRunEventDetails>) => void,
) => {
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  useEffect(() => {
    const handler = (event: Event) => {
      callbackRef.current(
        event as unknown as CustomEvent<StopTestRunEventDetails>,
      );
    };

    document.addEventListener(STOP_TEST_RUN_EVENT, handler);

    return () => {
      document.removeEventListener(STOP_TEST_RUN_EVENT, handler);
    };
  }, []);
};

export const useDispatchStopTestRunEvent = () => {
  return useCallback((source: StopTestRunEventDetails["source"]) => {
    document.dispatchEvent(
      new CustomEvent<StopTestRunEventDetails>(STOP_TEST_RUN_EVENT, {
        detail: { source },
      }),
    );
  }, []);
};

export const buildTestRunParamValue = (jobRun: Pick<JobRun, "job_id" | "id">) =>
  `${jobRun.job_id}_${jobRun.id}`;

export const useTestRunParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const testRunParam = searchParams.get(URLKeys.TestRunId);

  const setTestRunParam = useCallback(
    (jobRun: JobRun, openSidepane: boolean = false) => {
      setSearchParams((params) => {
        params.set(URLKeys.TestRunId, buildTestRunParamValue(jobRun));
        if (openSidepane) {
          params.set(URLKeys.LeftPane, LeftPaneOptions.TestRuns);
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const clearTestRunParam = useCallback(() => {
    setSearchParams((params) => {
      params.delete(URLKeys.TestRunId);
      return params;
    });
  }, [setSearchParams]);

  const parsedTestRunParam = useMemo(() => {
    const [jobId, runId] = testRunParam?.split("_") ?? [];
    return { jobId, runId };
  }, [testRunParam]);

  return {
    setTestRunParam,
    clearTestRunParam,
    currentTestRunParam: parsedTestRunParam,
  };
};

export const useTestRunJobRunInfo = (testRunJobRun?: TestRunJobRun) => {
  const { flow, workspace, orgId } = useAuthoringContext();
  const { lastRunDatasetId } = useGraphStore();

  const datasets = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  const user = useOrganizationUser(orgId, testRunJobRun?.trigger.triggered_by);

  if (!testRunJobRun) return undefined;

  const datasetName =
    datasets.data?.find((d) => d.id === lastRunDatasetId)?.name ??
    "Unknown Dataset";
  const startedBy = user.data?.email ?? "Unknown User";
  const startedAt = formatDate(
    testRunJobRun.created_at,
    "MMM d, yyyy 'at' h:mm a",
  );

  return { datasetName, startedBy, startedAt };
};

export const useOutputNodeJobRunCounts = (jobRunId: string) => {
  const data = useMetricsQueryData(jobRunId);
  const { nodesArray } = useGraphStore();
  const outputNodeId =
    nodesArray.find((n) => n.type === NODE_TYPE.OUTPUT_NODE)?.id ?? "";

  if (!data) {
    return undefined;
  }

  const {
    totalSuccess,
    totalFailed,
    totalIgnored,
    totalOther,
    totalPending,
    totalOutputMismatch,
  } = aggregateStatusCounts(data.status_counts, outputNodeId);

  return {
    success: totalSuccess,
    failed: totalFailed,
    ignored: totalIgnored,
    outputMismatch: totalOutputMismatch,
    total:
      totalSuccess +
      totalFailed +
      totalIgnored +
      totalOther +
      totalPending +
      totalOutputMismatch,
  };
};
